import secureStorage from '../lib/secureStorage';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import authedFetch from '../lib/authedFetch';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Activity, TrendingUp, TrendingDown, Target, Users, Award, Zap, AlertTriangle,
  CheckCircle2, AlertCircle, ChevronRight, ArrowUp, ArrowDown, Minus, Info,
  RefreshCw, Download, Loader2, PieChart, Gauge, Settings, Pin, PinOff,
  BarChart3, LineChart, Layers, DollarSign, Brain, Eye, EyeOff, Sparkles,
  ChevronDown, ChevronUp, Filter, Calendar, Building2, ShieldCheck, Mail, Database
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip,
  Cell, Legend, LineChart as RechartsLine, Line, ScatterChart, Scatter, ZAxis
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useDeepLinkOrg } from '../hooks/useDeepLinkOrg';
import logger from '../lib/logger';
import { TileInfoButton } from '../components/common/TileInfoButton';
import { useQuarterDelta } from '../hooks/useQuarterDelta';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import CollapsibleSection from '../components/layout/CollapsibleSection';
import PageToolbar from '../components/layout/PageToolbar';
import ChainPositionStrip from '../components/layout/ChainPositionStrip';
import { getSectionTheme, getLightSectionTheme } from '../lib/sectionThemes';
import { useTheme } from '../context/ThemeContext';
import PageSubNav from '../components/layout/PageSubNav';
import LeiLeanOverview, { LeiDeepDive, LeiTrend } from '../components/lei/LeiLeanOverview';
import CohortComparisonView, { EndingSoonNudge } from '../components/lei/CohortComparisonView';
import InvestmentView from '../components/lei/InvestmentView';
import GoalRing from '../components/lei/GoalRing';
import ForecastView from '../components/lei/ForecastView';
import ScoreTrendsView from '../components/lei/ScoreTrendsView';
import { TakeActionButton, buildLeiFocus } from '../components/health/HealthActionSpine';

const LEI_SUBNAV = [
  { id: 'overview', label: 'Overview', to: '/lei-health', end: true },
  { id: 'deep-dives', label: 'Pillars', to: '/lei-health/deep-dives' },
  { id: 'priorities', label: 'Priorities', to: '/lei-health/priorities' },
  { id: 'cohorts', label: 'Cohorts', to: '/lei-health/cohorts' },
  { id: 'programs', label: 'Programs', to: '/lei-health/programs' },
  { id: 'investment', label: 'Investment', to: '/lei-health/investment' },
  { id: 'forecast', label: 'Forecast', to: '/lei-health/forecast' },
  { id: 'score-trends', label: 'Score Trends', to: '/lei-health/score-trends' },
  { id: 'correlation', label: 'Correlation', to: '/lei-health/correlation' },
];
// Maps each top sub-nav segment to the underlying tab content.
// 'deep-dives' now surfaces the Pillars content and 'priorities'
// surfaces the Risks content (Pillars/Risks are no longer their
// own sub-nav entries).
const LEI_SUBVIEW_TO_TAB = {
  'deep-dives': 'pillars',
  'priorities': 'risks',
  'correlation': 'correlation',
  'cohorts': 'cohorts',
  'programs': 'programs',
  'investment': 'investment',
  'forecast': 'forecast',
  'score-trends': 'score-trends',
};
import WeakLinkCallout from '../components/lei/WeakLinkCallout';
import LEIActionPlanCard from '../components/lei/LEIActionPlanCard';
import LeiModePopoverButton from '../components/lei/LeiModePopoverButton';
import MeasureAuditDrawer from '../components/lei/MeasureAuditDrawer';
import AuditPackDialog from '../components/lei/AuditPackDialog';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pillar color mapping
const PILLAR_COLORS = {
  P1: '#3B82F6', // Blue - Access & Engagement
  P2: '#10B981', // Green - Performance & Outcomes
  P3: '#8B5CF6', // Purple - Capability & Readiness
  P4: '#F59E0B', // Amber - Efficiency
  P5: '#EF4444'  // Red - Business Impact
};

const PILLAR_NAMES = {
  P1: 'Learning Access & Engagement',
  P2: 'Learning Performance & Outcomes',
  P3: 'Workforce Capability & Readiness',
  P4: 'Operational Efficiency',
  P5: 'Business Impact & Value Realization'
};

const STATUS_COLORS = {
  excellent: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', color: '#10B981' },
  good: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', color: '#3B82F6' },
  fair: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', color: '#F59E0B' },
  poor: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', color: '#EF4444' }
};

// Helper function to get status from score
const getScoreStatus = (score) => {
  if (score >= 80) return 'excellent';
  if (score >= 65) return 'good';
  if (score >= 50) return 'fair';
  return 'poor';
};

// Trend indicator component
const TrendIndicator = ({ value, suffix = '%' }) => {
  if (value > 0) {
    return (
      <span className="flex items-center text-emerald-600 text-sm font-medium">
        <ArrowUp size={14} className="mr-0.5" />
        +{value.toFixed(1)}{suffix}
      </span>
    );
  } else if (value < 0) {
    return (
      <span className="flex items-center text-rose-600 text-sm font-medium">
        <ArrowDown size={14} className="mr-0.5" />
        {value.toFixed(1)}{suffix}
      </span>
    );
  }
  return (
    <span className="flex items-center text-gray-500 text-sm">
      <Minus size={14} className="mr-0.5" />
      0{suffix}
    </span>
  );
};

// Overall Health Score Gauge Component
const HealthScoreGauge = ({ score, trend, label = "LEI Health Score" }) => {
  const status = getScoreStatus(score);
  const statusColor = STATUS_COLORS[status].color;
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-36 h-36">
        <svg className="w-full h-full transform -rotate-90">
          {/* Background circle */}
          <circle
            cx="72"
            cy="72"
            r="45"
            fill="none"
            stroke="#E5E7EB"
            strokeWidth="10"
          />
          {/* Score circle */}
          <circle
            cx="72"
            cy="72"
            r="45"
            fill="none"
            stroke={statusColor}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold" style={{ color: statusColor }}>
            {score.toFixed(0)}
          </span>
          <span className="text-xs text-gray-500">/ 100</span>
        </div>
      </div>
      <div className="mt-2 text-center">
        <p className="text-sm font-medium text-gray-700">{label}</p>
        {trend !== undefined && <TrendIndicator value={trend} />}
      </div>
    </div>
  );
};

// Pillar Health Card Component
const PillarHealthCard = ({ pillar, score, trend, measures, isExpanded, onToggle, pinnedMeasures, onPinMeasure, confidenceMap = {}, onOpenAudit, highlight = false }) => {
  const status = getScoreStatus(score);
  const statusConfig = STATUS_COLORS[status];
  
  return (
    <Card data-testid={`lei-pillar-card-${pillar.id}`} className={cn(`${statusConfig.bg} ${statusConfig.border} border-l-4 transition-shadow duration-500`, highlight && 'ring-2 ring-offset-2 ring-[#44b6d2] shadow-lg')} style={{ borderLeftColor: pillar.color }}>
      <CardHeader className="pb-2 cursor-pointer" onClick={onToggle} data-testid={`lei-pillar-toggle-${pillar.id}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: pillar.color + '20' }}>
              <Activity size={20} style={{ color: pillar.color }} />
            </div>
            <div>
              <CardTitle className="text-base">{pillar.short_name}</CardTitle>
              <CardDescription className="text-xs">{Math.round(pillar.weight * 100)}% weight</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <span className="text-2xl font-bold" style={{ color: pillar.color }}>{score.toFixed(0)}</span>
              <TrendIndicator value={trend} />
            </div>
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        </div>
      </CardHeader>
      
      {isExpanded && (
        <CardContent className="pt-2">
          <div className="space-y-2">
            {measures.map((measure) => (
              <div key={measure.id} data-testid={`lei-measure-${measure.id}`} className="flex items-center justify-between p-2 bg-white rounded-lg">
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); onPinMeasure(measure.id); }}
                    className={`p-1 rounded ${pinnedMeasures.includes(measure.id) ? 'text-amber-500' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    {pinnedMeasures.includes(measure.id) ? <Pin size={14} /> : <PinOff size={14} />}
                  </button>
                  {(() => {
                    const pv = measure.provenance || 'none';
                    const meta = pv === 'measured'
                      ? { c: '#059669', t: 'Measured — computed from imported LMS/raw data' }
                      : pv === 'modeled'
                        ? { c: '#d97706', t: 'Modeled — synthetic baseline (not yet LMS-backed)' }
                        : { c: '#cbd5e1', t: 'No data' };
                    return (
                      <span
                        data-testid={`lei-measure-provenance-${measure.id}`}
                        title={meta.t}
                        className="inline-block h-2 w-2 rounded-full shrink-0"
                        style={{ backgroundColor: meta.c }}
                        aria-label={meta.t}
                      />
                    );
                  })()}
                  <span className="text-sm text-gray-700">{measure.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  {(() => {
                    const conf = confidenceMap[measure.id];
                    if (!conf) return null;
                    const tone = conf.confidence >= 0.75 ? 'text-emerald-600' : conf.confidence >= 0.5 ? 'text-amber-600' : 'text-rose-500';
                    return (
                      <span className={`text-[11px] ${tone}`} title={`Confidence ${Math.round(conf.confidence * 100)}% — recency ${Math.round(conf.factors.recency*100)}, sample ${Math.round(conf.factors.sample_size*100)}, completeness ${Math.round(conf.factors.completeness*100)}`}
                        data-testid={`lei-measure-confidence-${measure.id}`}>
                        ±{conf.band}
                      </span>
                    );
                  })()}
                  <span className="text-sm font-semibold">{measure.score?.toFixed(0) || '--'}</span>
                  {measure.trend !== undefined && <TrendIndicator value={measure.trend} />}
                  <button
                    onClick={(e) => { e.stopPropagation(); onOpenAudit && onOpenAudit(measure.id); }}
                    className="p-1 rounded text-gray-300 hover:text-cyan-600"
                    title="Where did this number come from?"
                    data-testid={`lei-measure-audit-${measure.id}`}
                  >
                    <Info size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// AI Insights Panel Component
const AIInsightsPanel = ({ insights, loading }) => {
  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-indigo-50 to-purple-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="text-indigo-600" size={20} />
            AI Insights
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="animate-spin text-indigo-600" size={24} />
            <span className="ml-2 text-gray-600">Analyzing your LEI data...</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="bg-gradient-to-br from-indigo-50 to-purple-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="text-indigo-600" size={20} />
          AI Insights & Recommendations
        </CardTitle>
        <CardDescription>Powered by AI analysis of your LEI health data</CardDescription>
      </CardHeader>
      <CardContent>
        {insights ? (
          <div className="space-y-4">
            {insights.summary && (
              <div className="p-3 bg-white rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-1">Summary</h4>
                <p className="text-sm text-gray-600">{insights.summary}</p>
              </div>
            )}
            {insights.recommendations && insights.recommendations.length > 0 && (
              <div className="p-3 bg-white rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Key Recommendations</h4>
                <ul className="space-y-2">
                  {insights.recommendations.map((rec, idx) => (
                    <li key={`rec-${rec.slice(0, 30)}-${idx}`} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle2 size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {insights.risks && insights.risks.length > 0 && (
              <div className="p-3 bg-white rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">Risk Areas</h4>
                <ul className="space-y-2">
                  {insights.risks.map((risk, idx) => (
                    <li key={`risk-${idx}-${risk.slice(0,15)}`} className="flex items-start gap-2 text-sm text-gray-600">
                      <AlertTriangle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
                      {risk}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">No insights available yet. Add more LEI data to generate insights.</p>
        )}
      </CardContent>
    </Card>
  );
};

// Correlation Heatmap Component
const CorrelationHeatmap = ({ correlations }) => {
  if (!correlations || correlations.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <p>No correlation data available</p>
      </div>
    );
  }
  
  const getCorrelationColor = (value) => {
    if (value >= 0.7) return '#10B981';
    if (value >= 0.4) return '#3B82F6';
    if (value >= 0.1) return '#F59E0B';
    if (value >= -0.1) return '#9CA3AF';
    return '#EF4444';
  };
  
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="p-2 text-left">LEI Measure</th>
            <th className="p-2 text-center">Productivity</th>
            <th className="p-2 text-center">Quality</th>
            <th className="p-2 text-center">Satisfaction</th>
            <th className="p-2 text-center">Engagement</th>
            <th className="p-2 text-center">Revenue</th>
          </tr>
        </thead>
        <tbody>
          {correlations.map((row) => (
            <tr key={row.measure} className="border-t">
              <td className="p-2 font-medium">{row.measure}</td>
              {row.kpis.map((kpi) => (
                <td key={`${row.measure}-${kpi.name}`} className="p-2 text-center">
                  <div
                    className="w-12 h-8 mx-auto rounded flex items-center justify-center text-white font-semibold text-xs"
                    style={{ backgroundColor: getCorrelationColor(kpi.value) }}
                  >
                    {(kpi.value * 100).toFixed(0)}%
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Risk Item Component
const RiskItem = ({ risk, navigate }) => {
  const getSeverityConfig = (severity) => {
    switch (severity) {
      case 'high': return { bg: 'bg-rose-100', text: 'text-rose-700', icon: AlertTriangle };
      case 'medium': return { bg: 'bg-amber-100', text: 'text-amber-700', icon: AlertCircle };
      default: return { bg: 'bg-blue-100', text: 'text-blue-700', icon: Info };
    }
  };
  
  const config = getSeverityConfig(risk.severity);
  const Icon = config.icon;
  
  return (
    <div className={`p-4 rounded-lg ${config.bg}`}>
      <div className="flex items-start gap-3">
        <Icon className={config.text} size={20} />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <h4 className={`font-semibold ${config.text}`}>{risk.measure}</h4>
            <Badge variant="outline" className={config.text}>{risk.severity}</Badge>
          </div>
          <p className="text-sm text-gray-600 mt-1">{risk.description}</p>
          {risk.recommendation && (
            <p className="text-sm text-gray-700 mt-2">
              <strong>Recommendation:</strong> {risk.recommendation}
            </p>
          )}
          {risk.measure_id && navigate && (
            <button
              onClick={() => navigate('/configure', { state: { focusMeasureId: risk.measure_id, focusMeasureName: risk.measure, focusReason: 'lei-risk-area' } })}
              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 hover:text-indigo-900"
              data-testid={`risk-model-${risk.measure_id}`}
            >
              Model this in Single Measure ROI <ChevronRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Component
export default function LEIHealthDashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme !== 'light';
  const segs = location.pathname.split('/').filter(Boolean);
  const subView = segs[1] || 'overview';
  const pillarId = subView === 'deep-dives' ? (segs[2] || null) : null;
  const mappedTab = LEI_SUBVIEW_TO_TAB[subView] || null;
  const showTabBody = !!mappedTab;
  React.useEffect(() => { if (mappedTab) setActiveTab(mappedTab); }, [mappedTab]);
  // Drilling into a specific pillar (from the Overview breakdown)
  // now lands on the Pillars content with that pillar pre-expanded,
  // scrolled into view and briefly highlighted so the target is
  // immediately obvious.
  React.useEffect(() => {
    if (!pillarId) return;
    setExpandedPillars(prev => (prev.includes(pillarId) ? prev : [...prev, pillarId]));
    setHighlightPillar(pillarId);
    const scrollTimer = setTimeout(() => {
      const el = document.querySelector(`[data-testid="lei-pillar-card-${pillarId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 400);
    const clearTimer = setTimeout(() => setHighlightPillar(null), 2800);
    return () => { clearTimeout(scrollTimer); clearTimeout(clearTimer); };
  }, [pillarId]);
  // Deep-link from the Overview "Top Priority" tile: land on the
  // Priorities (Risk & Gap) view scrolled to the Risk Areas / Action
  // Plan surface so the recommended plan is immediately visible.
  React.useEffect(() => {
    if (subView !== 'priorities') return;
    const params = new URLSearchParams(location.search);
    if (params.get('focus') !== 'action-plan') return;
    const t = setTimeout(() => {
      const el = document.querySelector('[data-testid="lei-priorities-action-plan"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 500);
    return () => clearTimeout(t);
  }, [subView, location.search]);
  const { isSuperAdmin, viewAsOrg, setViewAsOrg, clearViewAsOrg, rbac, userRole, loading: authLoading } = useAuth();
  // Deep-link: ?organization_id= scopes the page and (for super admins)
  // aligns the global "Previewing as" banner. Backend guards tenant access.
  const urlOrgId = useDeepLinkOrg();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [healthData, setHealthData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(urlOrgId || viewAsOrg?.id || rbac?.organization_id || 'all');
  const [organizations, setOrganizations] = useState([]);
  const [selectedCohort, setSelectedCohort] = useState('all');
  const [cohorts, setCohorts] = useState([]);
  const [expandedPillars, setExpandedPillars] = useState(['P1']);
  // The in-page org filter drives the SAME view-as-org scoping the header uses
  // (X-View-As-Org), so switching it actually re-scopes the data instead of
  // blanking it. 'all' clears the preview (super admin sees every org).
  const handleOrgFilterChange = (v) => {
    setSelectedOrg(v);
    if (v === 'all') {
      clearViewAsOrg && clearViewAsOrg();
    } else {
      const o = organizations.find((x) => x.id === v);
      setViewAsOrg && setViewAsOrg(v, o?.name || v);
    }
  };
  // Default the in-page filter to the user's own org (e.g. Seertech) instead of
  // 'All Organizations', while still syncing to later HEADER org-switcher changes.
  // A single guarded effect avoids the mount race where a separate sync effect
  // would overwrite the one-time default back to 'all'.
  const didInitOrgRef = useRef(false);
  useEffect(() => {
    if (!didInitOrgRef.current) {
      // Wait until RBAC is available so we know the user's own org.
      if (!rbac?.organization_id && !viewAsOrg?.id) return;
      didInitOrgRef.current = true;
      if (!urlOrgId) setSelectedOrg(viewAsOrg?.id || rbac?.organization_id || 'all');
      return;
    }
    // Subsequent runs only sync to a REAL header selection. When there is no
    // active preview (viewAsOrg null) we leave selectedOrg untouched — this is
    // StrictMode-safe (the double-invoked effect won't reset the default) and
    // preserves an explicit 'All Organizations' pick.
    if (viewAsOrg?.id) {
      setSelectedOrg((prev) => (prev === viewAsOrg.id ? prev : viewAsOrg.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAsOrg?.id, rbac?.organization_id]);

  const [highlightPillar, setHighlightPillar] = useState(null);
  // Org scope is "ready" once we know the user's own org (or a URL/preview org
  // is explicit). Until then, org-scoped comparison views must NOT fetch with
  // 'all' — for a super admin that briefly returned a cross-tenant blend and
  // polluted the saved cohort filter with other orgs' ids.
  const orgReady = !!urlOrgId || !!viewAsOrg?.id || !!rbac?.organization_id || (!authLoading && !!rbac);
  // Expand every pillar by default the first time scores load, so the
  // Deep Dives page opens fully expanded (user can still collapse any).
  const didInitExpand = React.useRef(false);
  React.useEffect(() => {
    if (didInitExpand.current) return;
    const ids = (healthData?.pillars || []).map(p => p.id);
    if (ids.length) { setExpandedPillars(ids); didInitExpand.current = true; }
  }, [healthData]);
  const [pinnedMeasures, setPinnedMeasures] = useState([]);
  const [timeRange, setTimeRange] = useState('6m');
  const [confidenceMap, setConfidenceMap] = useState({});
  const [coverageRollup, setCoverageRollup] = useState(null);
  const [auditMeasureId, setAuditMeasureId] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const openAudit = useCallback((mid) => { setAuditMeasureId(mid); setAuditOpen(true); }, []);
  const [downloadingPack, setDownloadingPack] = useState(false);
  const [packDialogOpen, setPackDialogOpen] = useState(false);
  const [packDialogTab, setPackDialogTab] = useState('email');
  const openPackDialog = useCallback((t) => { setPackDialogTab(t); setPackDialogOpen(true); }, []);
  const downloadAuditPack = useCallback(async () => {
    setDownloadingPack(true);
    try {
      const params = new URLSearchParams();
      if (selectedOrg && selectedOrg !== 'all') params.set('organization_id', selectedOrg);
      const r = await authedFetch(`${API_URL}/api/lei-raw/audit-pack/pdf?${params}`, {});
      if (!r.ok) throw new Error('pack');
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'lei_audit_pack.pdf'; a.click();
      URL.revokeObjectURL(url);
      toast.success('Audit pack downloaded');
    } catch (e) {
      toast.error('Could not generate audit pack');
    } finally {
      setDownloadingPack(false);
    }
  }, [selectedOrg]);
  const leiQuarterDelta = useQuarterDelta('lei');
  
  // Fetch organizations (Super Admin only)
  useEffect(() => {
    if (!isSuperAdmin) return;
    const fetchOrgs = async () => {
      const token = secureStorage.getItem('session_token');
      try {
        const res = await authedFetch(`${API_URL}/api/rbac/organizations`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setOrganizations(data.organizations || []);
        }
      } catch (e) { logger.error('Error fetching orgs:', e); }
    };
    fetchOrgs();
  }, [isSuperAdmin]);
  
  // Reset cohort when org changes
  useEffect(() => {
    setSelectedCohort('all');
  }, [selectedOrg]);
  
  // Filtered cohorts by org
  const filteredCohorts = selectedOrg === 'all' ? cohorts : cohorts.filter(c => c.organization_id === selectedOrg);
  
  // Fetch health data
  const fetchHealthData = useCallback(async () => {
    setLoading(true);
    try {
      const token = secureStorage.getItem('session_token');
      const params = new URLSearchParams({
        time_range: timeRange,
        ...(selectedCohort !== 'all' && { cohort_id: selectedCohort }),
        ...(selectedOrg && selectedOrg !== 'all' && { organization_id: selectedOrg })
      });
      
      const response = await authedFetch(`${API_URL}/api/lei-health/overview?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setHealthData(data);
      } else {
        // Generate mock data for demo
        setHealthData(generateMockHealthData());
      }
    } catch (error) {
      logger.error('Error fetching health data:', error);
      setHealthData(generateMockHealthData());
    } finally {
      setLoading(false);
    }
  }, [timeRange, selectedCohort, selectedOrg]);
  
  // Fetch cohorts
  const fetchCohorts = useCallback(async () => {
    try {
      const token = secureStorage.getItem('session_token');
      const params = new URLSearchParams();
      if (selectedOrg && selectedOrg !== 'all') {
        params.append('organization_id', selectedOrg);
      }
      const response = await authedFetch(`${API_URL}/api/segments/cohorts?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCohorts(data.cohorts || []);
      }
    } catch (error) {
      /* cohort fetch error */
    }
  }, [selectedOrg]);
  
  // Fetch AI insights
  const fetchInsights = useCallback(async () => {
    setInsightsLoading(true);
    try {
      const token = secureStorage.getItem('session_token');
      const params = new URLSearchParams({
        cohort_id: selectedCohort,
        ...(selectedOrg && selectedOrg !== 'all' && { organization_id: selectedOrg })
      });
      const response = await authedFetch(`${API_URL}/api/lei-health/insights?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInsights(data);
      } else {
        setInsights(generateMockInsights());
      }
    } catch (error) {
      logger.error('Error fetching insights:', error);
      setInsights(generateMockInsights());
    } finally {
      setInsightsLoading(false);
    }
  }, [selectedCohort, selectedOrg]);
  
  useEffect(() => {
    fetchHealthData();
    fetchCohorts();
  }, [fetchHealthData, fetchCohorts]);

  // Fetch per-measure confidence bands (R9 phase 2) + component coverage rollup
  useEffect(() => {
    (async () => {
      try {
        const params = new URLSearchParams();
        if (selectedOrg && selectedOrg !== 'all') params.append('organization_id', selectedOrg);
        const [rc, rcov] = await Promise.all([
          authedFetch(`${API_URL}/api/lei-raw/confidence?${params}`, {}),
          authedFetch(`${API_URL}/api/lei-raw/component-coverage?${params}`, {}),
        ]);
        if (rc.ok) setConfidenceMap((await rc.json()).confidence || {});
        if (rcov.ok) setCoverageRollup(await rcov.json());
      } catch (e) { /* decorative */ }
    })();
  }, [selectedOrg, healthData]);
  
  useEffect(() => {
    if (activeTab === 'overview' && healthData) {
      fetchInsights();
    }
  }, [activeTab, healthData, selectedCohort, fetchInsights]);
  
  // Load pinned measures from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('lei_pinned_measures');
    if (saved) {
      setPinnedMeasures(JSON.parse(saved));
    }
  }, []);
  
  // Save pinned measures to localStorage
  const handlePinMeasure = (measureId) => {
    setPinnedMeasures(prev => {
      const newPinned = prev.includes(measureId)
        ? prev.filter(id => id !== measureId)
        : [...prev, measureId];
      localStorage.setItem('lei_pinned_measures', JSON.stringify(newPinned));
      return newPinned;
    });
  };
  
  const togglePillar = (pillarId) => {
    setExpandedPillars(prev =>
      prev.includes(pillarId)
        ? prev.filter(id => id !== pillarId)
        : [...prev, pillarId]
    );
  };
  
  // Mock data generators
  const generateMockHealthData = () => ({
    overall_score: 72.4,
    overall_trend: 3.2,
    status: 'good',
    pillars: [
      {
        id: 'P1',
        name: 'Learning Access & Engagement',
        short_name: 'Access & Engagement',
        color: '#3B82F6',
        weight: 0.20,
        score: 78.5,
        trend: 4.2,
        measures: [
          { id: 'training_scope', name: 'Training Scope Index', score: 82, trend: 3.1 },
          { id: 'sme_training', name: 'SME Training Scope', score: 71, trend: -2.4 },
          { id: 'community_learning', name: 'Community Learning', score: 68, trend: 5.8 },
          { id: 'voluntary_engagement', name: 'Voluntary Engagement', score: 85, trend: 2.1 },
          { id: 'tech_utilization', name: 'Tech Utilization', score: 79, trend: 6.3 },
          { id: 'learning_culture', name: 'Learning Culture', score: 76, trend: 1.5 }
        ]
      },
      {
        id: 'P2',
        name: 'Learning Performance & Outcomes',
        short_name: 'Performance & Outcomes',
        color: '#10B981',
        weight: 0.30,
        score: 74.2,
        trend: 2.8,
        measures: [
          { id: 'training_performance', name: 'Training Performance', score: 76, trend: 3.5 },
          { id: 'nps', name: 'Learning NPS', score: 72, trend: 1.2 },
          { id: 'skills_assessment', name: 'Skills Assessment', score: 74, trend: 3.8 }
        ]
      },
      {
        id: 'P3',
        name: 'Workforce Capability & Readiness',
        short_name: 'Capability & Readiness',
        color: '#8B5CF6',
        weight: 0.20,
        score: 68.7,
        trend: -1.5,
        measures: [
          { id: 'skills_coverage', name: 'Skills Coverage', score: 72, trend: 2.1 },
          { id: 'certification_depth', name: 'Certification Depth', score: 65, trend: -3.2 },
          { id: 'readiness_index', name: 'Readiness Index', score: 69, trend: -3.4 }
        ]
      },
      {
        id: 'P4',
        name: 'Operational Efficiency',
        short_name: 'Efficiency',
        color: '#F59E0B',
        weight: 0.20,
        score: 71.3,
        trend: 5.1,
        measures: [
          { id: 'cost_efficiency', name: 'Cost Efficiency', score: 68, trend: 4.2 },
          { id: 'scheduling_velocity', name: 'Scheduling Velocity', score: 75, trend: 6.0 }
        ]
      },
      {
        id: 'P5',
        name: 'Business Impact & Value',
        short_name: 'Business Impact',
        color: '#EF4444',
        weight: 0.10,
        score: 69.8,
        trend: 2.4,
        measures: [
          { id: 'kpi_alignment', name: 'KPI Alignment', score: 70, trend: 2.4 }
        ]
      }
    ],
    trend_data: Array.from({ length: 6 }, (_, i) => ({
      month: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'][i],
      score: 65 + Math.random() * 15,
      P1: 70 + Math.random() * 15,
      P2: 68 + Math.random() * 15,
      P3: 62 + Math.random() * 15,
      P4: 66 + Math.random() * 15,
      P5: 64 + Math.random() * 15
    })),
    correlations: [
      { measure: 'Training Performance', kpis: [{ value: 0.82 }, { value: 0.65 }, { value: 0.71 }, { value: 0.58 }, { value: 0.45 }] },
      { measure: 'Skills Assessment', kpis: [{ value: 0.75 }, { value: 0.78 }, { value: 0.62 }, { value: 0.55 }, { value: 0.68 }] },
      { measure: 'Learning Culture', kpis: [{ value: 0.45 }, { value: 0.52 }, { value: 0.68 }, { value: 0.85 }, { value: 0.42 }] },
      { measure: 'Cost Efficiency', kpis: [{ value: 0.38 }, { value: 0.42 }, { value: 0.35 }, { value: 0.28 }, { value: 0.72 }] }
    ],
    risks: [
      { measure: 'Certification Depth', severity: 'high', description: 'Score dropped 3.2% this month, now 15 points below target', recommendation: 'Review certification program completion rates and identify barriers' },
      { measure: 'Readiness Index', severity: 'high', description: 'Declining trend for 3 consecutive months', recommendation: 'Conduct skills gap analysis and accelerate critical training paths' },
      { measure: 'SME Training', severity: 'medium', description: 'Below benchmark by 8 points', recommendation: 'Increase SME engagement through targeted micro-learning' }
    ],
    investment_data: {
      total_investment: 2450000,
      roi_percentage: 185,
      cost_per_point: 12500,
      efficiency_trend: 8.5
    }
  });
  
  const generateMockInsights = () => ({
    summary: "Your LEI health score of 72.4 indicates 'Good' overall performance with positive momentum. The strongest pillar is Access & Engagement (78.5), while Capability & Readiness (68.7) needs attention due to declining certification rates.",
    recommendations: [
      "Focus on Certification Depth - implement completion incentives and remove barriers to certification",
      "Leverage high voluntary engagement (85) by introducing peer-led learning initiatives",
      "Build on strong tech utilization trend (+6.3%) to drive digital learning adoption"
    ],
    risks: [
      "Readiness Index declining for 3 months - may impact operational performance",
      "SME training below benchmark - knowledge transfer gaps possible"
    ]
  });

  const theme = isDark
    ? { ...getSectionTheme('LEI'), heroGradient: 'bg-[#0a0f1c]', bodyTint: 'bg-[#0a0f1c]', textBase: 'text-white', textLighter: 'text-slate-400', titleClass: 'text-slate-50 font-extrabold' }
    : getLightSectionTheme('LEI');

  const heroActions = (
    <>
      <PageToolbar theme={theme} collapsePrefix="lei" organizationId={selectedOrg !== 'all' ? selectedOrg : ''} testId="lei-toolbar" />
      <Select value={timeRange} onValueChange={setTimeRange}>
        <SelectTrigger className="w-32 bg-white border-slate-200 text-slate-700">
          <SelectValue placeholder="Time Range" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="3m">Last 3 months</SelectItem>
          <SelectItem value="6m">Last 6 months</SelectItem>
          <SelectItem value="12m">Last 12 months</SelectItem>
        </SelectContent>
      </Select>
      {isSuperAdmin && organizations.length > 0 && (
        <Select value={selectedOrg} onValueChange={handleOrgFilterChange}>
          <SelectTrigger className="w-44 bg-white border-slate-200 text-slate-700" data-testid="health-org-filter">
            <SelectValue placeholder="All Organizations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Organizations</SelectItem>
            {organizations.map(o => (
              <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <Select value={selectedCohort} onValueChange={setSelectedCohort}>
        <SelectTrigger className="w-44 bg-white border-slate-200 text-slate-700" data-testid="health-cohort-filter">
          <SelectValue placeholder="Select Cohort" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Cohorts</SelectItem>
          {filteredCohorts.map(cohort => (
            <SelectItem key={cohort.id} value={cohort.id}>
              {cohort.name}{cohort.organization_name && cohort.organization_id !== 'demo' ? ` · ${cohort.organization_name}` : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button variant="outline" onClick={fetchHealthData} className="bg-white border-slate-200 text-slate-700 hover:bg-slate-50">
        <RefreshCw size={16} className="mr-2" />
        Refresh
      </Button>
    </>
  );

  // Rich hero tiles — match the Maturity Health pattern. Lifts the
  // big Score Gauge + Top Risk + Strongest Pillar tiles into the
  // green hero so the Overview tab can lead with the radar + trend
  // chart instead of duplicating the headline number.
  const overallScore = Math.round(healthData?.overall_score || 0);
  const overallTrend = healthData?.overall_trend ?? 0;
  const scoreStatus = getScoreStatus(overallScore);
  const sortedPillars = (healthData?.pillars || []).slice().sort((a, b) => (b.score || 0) - (a.score || 0));
  const strongestPillar = sortedPillars[0] || null;
  const weakestPillar = sortedPillars[sortedPillars.length - 1] || null;
  const allMeasuresFlat = (healthData?.pillars || []).flatMap(p => p.measures || []);
  const measuredCount = allMeasuresFlat.filter(m => m.provenance === 'measured').length;
  const dataConfidence = allMeasuresFlat.length ? Math.round((measuredCount / allMeasuresFlat.length) * 100) : 0;
  const confidenceTone = dataConfidence >= 66 ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : dataConfidence >= 33 ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-slate-50 text-slate-500 border-slate-200';
  const confVals = Object.values(confidenceMap || {});
  const avgConfidence = confVals.length ? Math.round(confVals.reduce((s, c) => s + (c.confidence || 0), 0) / confVals.length * 100) : null;
  const auditReady = dataConfidence === 100 && (avgConfidence == null || avgConfidence >= 60);
  // ── Hero stats — Option A treatment ─────────────────────────────
  // White cards with a soft outer ring + section-colored text. The
  // gauge stroke uses the same chroma as the score text so status
  // is single-encoded. Mirrors the LIE home pillar tiles.
  const overallTone = overallScore >= 75
    ? { stroke: '#06b6d4', text: 'text-cyan-700', ring: 'ring-cyan-200', border: 'border-cyan-200', dot: 'bg-cyan-500' }
    : overallScore >= 50
      ? { stroke: '#14b8a6', text: 'text-teal-700', ring: 'ring-teal-200', border: 'border-teal-200', dot: 'bg-teal-500' }
      : overallScore >= 25
        ? { stroke: '#f59e0b', text: 'text-amber-700', ring: 'ring-amber-200', border: 'border-amber-200', dot: 'bg-amber-500' }
        : { stroke: '#94a3b8', text: 'text-slate-600', ring: 'ring-slate-200', border: 'border-slate-200', dot: 'bg-slate-400' };

  const richHeroStats = (
    <div
      className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mt-6"
      data-testid="lei-hero-rich-tiles"
    >
      {/* Overall LEI gauge — white card, colored gauge stroke, score
          in the section-colored text token. */}
      <Card
        className={cn('shadow-sm bg-white border border-slate-200 ring-1', overallTone.ring)}
        data-testid="lei-hero-overall"
      >
        <CardContent className="pt-5 pb-4 text-center">
          <div className="relative w-32 h-32 mx-auto mb-2">
            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
              <circle cx="50" cy="50" r="42" fill="none" stroke="#e2e8f0" strokeWidth="5" />
              <circle cx="50" cy="50" r="42" fill="none" stroke={overallTone.stroke} strokeWidth="5"
                strokeDasharray={`${overallScore * 2.64} 264`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={cn('text-3xl font-extrabold leading-none', overallTone.text)}>{overallScore}</span>
              <span className="text-[9px] text-slate-500 uppercase tracking-widest font-semibold mt-1">Overall LEI</span>
            </div>
          </div>
          <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-white', overallTone.border, overallTone.text)}>
            <span className={cn('w-1 h-1 rounded-full', overallTone.dot)} />
            {scoreStatus} Health
          </span>
          <div
            className={cn('mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border', confidenceTone)}
            data-testid="lei-data-confidence-chip"
            title={`Data confidence: ${measuredCount} of ${allMeasuresFlat.length} measures are LMS/raw-backed (measured). The rest are modeled baselines.`}
          >
            <span className="w-1 h-1 rounded-full bg-current" />
            {dataConfidence}% measured
          </div>
          <div className="mt-2 flex flex-col items-center gap-1.5">
            {auditReady && (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border border-emerald-300 bg-emerald-50 text-emerald-700"
                data-testid="lei-audit-ready-badge"
                title="Every measure is LMS/raw-backed with a traceable audit trail and confidence band."
              >
                <ShieldCheck size={11} /> Audit-ready{avgConfidence != null ? ` · ${avgConfidence}% avg confidence` : ''}
              </span>
            )}
            {coverageRollup && coverageRollup.total_measures > 0 && (() => {
              const gaps = (coverageRollup.measures || []).filter(m => m.missing && m.missing.length).sort((a, b) => a.completeness - b.completeness);
              const full = coverageRollup.fully_captured === coverageRollup.total_measures;
              return (
                <button
                  type="button"
                  onClick={() => gaps.length && openAudit(gaps[0].measure_id)}
                  disabled={!gaps.length}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${full ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'}`}
                  data-testid="lei-coverage-rollup-chip"
                  title={gaps.length ? `Lowest: ${gaps[0].name} (${Math.round(gaps[0].completeness * 100)}% captured) — click to inspect` : 'All raw components captured'}
                >
                  <Database size={11} /> {coverageRollup.fully_captured}/{coverageRollup.total_measures} fully captured
                </button>
              );
            })()}
            <Button
              size="sm" variant="outline"
              onClick={downloadAuditPack} disabled={downloadingPack}
              className="h-7 px-2 text-[11px] gap-1 border-slate-200 text-slate-600 hover:bg-slate-50"
              data-testid="lei-download-audit-pack-btn"
            >
              {downloadingPack ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
              Download audit pack
            </Button>
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="ghost" onClick={() => openPackDialog('email')}
                className="h-6 px-2 text-[11px] gap-1 text-slate-500 hover:text-emerald-700 hover:bg-emerald-50"
                data-testid="lei-email-audit-pack-btn">
                <Mail size={12} /> Email to auditor
              </Button>
              <span className="text-slate-300">·</span>
              <Button size="sm" variant="ghost" onClick={() => openPackDialog('verify')}
                className="h-6 px-2 text-[11px] gap-1 text-slate-500 hover:text-cyan-700 hover:bg-cyan-50"
                data-testid="lei-verify-audit-pack-btn">
                <ShieldCheck size={12} /> Verify
              </Button>
            </div>
          </div>
          {leiQuarterDelta ? (
            <div
              className={cn(
                'mt-2 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full',
                leiQuarterDelta.direction === 'up' && 'bg-emerald-50 text-emerald-700',
                leiQuarterDelta.direction === 'down' && 'bg-rose-50 text-rose-700',
                leiQuarterDelta.direction === 'flat' && 'bg-slate-50 text-slate-500',
              )}
              data-testid="lei-hero-quarter-delta"
              title={leiQuarterDelta.deltaText}
            >
              {leiQuarterDelta.direction === 'up' && '▲'}
              {leiQuarterDelta.direction === 'down' && '▼'}
              {leiQuarterDelta.direction === 'flat' && '▬'}
              {' '}{leiQuarterDelta.delta > 0 ? '+' : ''}{leiQuarterDelta.delta}% vs last qtr
            </div>
          ) : overallTrend !== 0 ? (
            <p className="text-[11px] text-slate-500 mt-1.5">
              {overallTrend > 0 ? '▲' : '▼'} {Math.abs(overallTrend).toFixed(1)}% vs last period
            </p>
          ) : null}
        </CardContent>
      </Card>

      {/* Weakest pillar — white card with rose accent. */}
      <Card className="shadow-sm bg-white border border-slate-200 ring-1 ring-rose-200" data-testid="lei-hero-risk">
        <CardContent className="pt-6 pb-5 text-center">
          <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-3">
            <AlertTriangle className="h-5 w-5 text-rose-600" />
          </div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Weakest Pillar
          </p>
          <p className="text-2xl font-extrabold text-slate-900 mt-1 truncate" title={weakestPillar?.name || weakestPillar?.short_name || '—'}>
            {weakestPillar?.short_name || weakestPillar?.name || '—'}
          </p>
          {weakestPillar && (
            <p className="text-sm text-rose-700 mt-1.5 font-semibold tabular-nums">Score: {Math.round(weakestPillar.score)} / 100</p>
          )}
          <p className="text-xs text-slate-500 mt-1">
            {healthData?.risks?.length ? `${healthData.risks.length} active risk${healthData.risks.length === 1 ? '' : 's'}` : 'No flagged risks'}
          </p>
        </CardContent>
      </Card>

      {/* Strongest pillar — white card with emerald accent. */}
      <Card className="shadow-sm bg-white border border-slate-200 ring-1 ring-emerald-200" data-testid="lei-hero-strength">
        <CardContent className="pt-6 pb-5 text-center">
          <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
            <TrendingUp className="h-5 w-5 text-emerald-600" />
          </div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Strongest Pillar</p>
          <p className="text-2xl font-extrabold text-slate-900 mt-1 truncate" title={strongestPillar?.short_name || strongestPillar?.name}>
            {strongestPillar?.short_name || strongestPillar?.name || '—'}
          </p>
          {strongestPillar && (
            <p className="text-sm text-emerald-700 mt-1.5 font-semibold tabular-nums">Score: {Math.round(strongestPillar.score)} / 100</p>
          )}
          <p className="text-xs text-slate-500 mt-1">
            {healthData?.pillars?.length ?? 0} pillars tracked
          </p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <PageShell
      theme={theme}
      hero={(
        <PageHero
          title="Learning Health"
          titleHint="LEI"
          description="Monitor and optimize your Learning Effectiveness Index (LEI) — the LEARNING stage of the Impact Chain. The composite score is built from 5 pillars and 27 measures."
          theme={theme}
          icon={Activity}
          actions={heroActions}
          pinSnapshot={[
            {
              label: 'Overall LEI',
              value: `${overallScore}/100`,
              hint: scoreStatus ? `${scoreStatus} Health` : undefined,
              ...(leiQuarterDelta ? {
                delta: leiQuarterDelta.delta,
                deltaText: leiQuarterDelta.deltaText,
                deltaLabel: leiQuarterDelta.deltaLabel,
              } : {}),
            },
            { label: 'Weakest Pillar', value: weakestPillar?.short_name || weakestPillar?.name || '—' },
            { label: 'Strongest Pillar', value: strongestPillar?.short_name || strongestPillar?.name || '—' },
          ]}
          testId="lei-hero"
        >
          {/* Light hero accents — thin teal brand rule + faint
              indigo/teal radial glow. Absolutely positioned within
              the hero wrapper so they sit behind the children
              without disrupting layout flow. Matches the LIE home
              treatment. */}
          <div
            className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-teal-400 via-teal-500 to-teal-400 pointer-events-none"
            aria-hidden="true"
          />
          <div
            className="absolute inset-0 opacity-[0.08] pointer-events-none"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 50%, #6366f1 0%, transparent 50%), ' +
                'radial-gradient(circle at 80% 50%, #14b8a6 0%, transparent 50%)',
            }}
            aria-hidden="true"
          />
        </PageHero>
      )}
    >
      <div data-testid="lei-page-info" className="hidden">
        <TileInfoButton
          testid="lei-page-info-inner"
          title="LEI — Learning Effectiveness Index"
          body="LEI answers 'is your learning system actually any good?' before you ask whether anyone learned, behaved, or moved a KPI."
          side="bottom"
        />
      </div>
      <div className="mt-6">
        {loading ? (
          <div className="flex items-center justify-center py-24" data-testid="lei-loading">
            <Loader2 className="animate-spin h-10 w-10 text-emerald-600 mr-3" />
            <p className="text-slate-600">Loading LEI Health Dashboard...</p>
          </div>
        ) : (
        <div className={cn('space-y-6 min-w-0', isDark ? 'home-dark-scope' : 'mc-light')}>
          <PageSubNav
            items={LEI_SUBNAV}
            rightSlot={subView === 'deep-dives' ? (
              <LeiModePopoverButton
                currentMode={healthData?.lei_mode || 'standalone'}
                selectedOrg={selectedOrg}
                isAdmin={isSuperAdmin || userRole === 'org_admin'}
                onCommitted={fetchHealthData}
              />
            ) : null}
          />
          {subView === 'overview' && (
            <>
              <EndingSoonNudge organizationId={selectedOrg} isDark={isDark} navigate={navigate} />
              <LeiLeanOverview data={healthData} navigate={navigate} selectedOrg={selectedOrg} />
            </>
          )}
          {showTabBody && (<>
          {/* Deep dives — the tab cluster. Each top sub-nav item
              routes straight to its content (Deep Dives → Pillars,
              Priorities → Risks), so the old inner tab strip and the
              Priority Focus block have been removed — the coaching /
              action-plan surface already lives on the Overview. */}
          <CollapsibleSection
            storageKey="lei.deepDives"
            title={`Explore the ${LEI_SUBNAV.find((i) => i.id === subView)?.label || 'Details'}`}
            accent="text-indigo-600"
            defaultExpanded
            testId="lei-deep-dives-section"
          >

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6 min-w-0">
          
          {/* Tab 1: Overview */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Pillar Summary — radar now spans 2 cols (was sharing space with the Score Gauge that's been lifted into the hero) */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    Pillar Performance
                    <TileInfoButton
                      testid="lei-radar-info"
                      title="Pillar Performance — radar view"
                      body="Each axis is one of the 5 Pillars. The blue area shows your current scores; the dashed gray pentagon is the 70-point benchmark. The shape matters: a balanced regular pentagon = uniform health, a lopsided shape tells you which pillar is dragging the headline LEI down. Switch to the Pillars tab to expand any pillar into its underlying measures."
                    />
                  </CardTitle>
                  <CardDescription>Health scores by pillar with trends</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height={256}>
                      <RadarChart data={healthData?.pillars?.map(p => ({
                        pillar: p.short_name,
                        score: p.score,
                        benchmark: 70
                      })) || []}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="pillar" tick={{ fontSize: 11 }} />
                        <PolarRadiusAxis domain={[0, 100]} />
                        <Radar name="Score" dataKey="score" stroke="#6366F1" fill="#6366F1" fillOpacity={0.5} />
                        <Radar name="Benchmark" dataKey="benchmark" stroke="#9CA3AF" fill="none" strokeDasharray="5 5" />
                        <Legend />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              {/* Trend Chart */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    Health Score Trend
                    <TileInfoButton
                      testid="lei-trend-info"
                      title="Health Score Trend"
                      body="Six-month time series of the headline Overall LEI score. Look for inflection points — a rising line after a content refresh validates the spend; a flat line during a heavy program quarter is a red flag (volume without effectiveness). Tooltip on any point shows the exact score; pair this with the Investment tab to overlay spend."
                    />
                  </CardTitle>
                  <CardDescription>6-month historical performance</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height={256}>
                      <AreaChart data={healthData?.trend_data || []}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis domain={[50, 100]} />
                        <Tooltip />
                        <Area type="monotone" dataKey="score" stroke="#6366F1" fill="#6366F1" fillOpacity={0.3} name="Overall Score" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
              
              {/* AI Insights */}
              <div className="lg:col-span-1">
                <AIInsightsPanel insights={insights} loading={insightsLoading} />
              </div>
            </div>
            
            {/* Pinned Measures */}
            {pinnedMeasures.length > 0 && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Pin size={18} className="text-amber-500" />
                    Your Pinned Measures
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {healthData?.pillars?.flatMap(p => p.measures)
                      .filter(m => pinnedMeasures.includes(m.id))
                      .map(measure => (
                        <div key={measure.id} className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                          <p className="text-sm text-gray-600">{measure.name}</p>
                          <div className="flex items-center justify-between mt-2">
                            <span className="text-2xl font-bold text-amber-700">{measure.score}</span>
                            <TrendIndicator value={measure.trend} />
                          </div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* Tab 2: Pillar Deep-Dive */}
          <TabsContent value="pillars">
            {(() => {
              const mode = healthData?.lei_mode || 'standalone';
              const eff = healthData?.effective_weights || {};
              const allPillars = healthData?.pillars || [];
              const p5r = healthData?.p5_reinforcement;
              const indexPillars = allPillars.filter(p => (mode === 'conjoined' ? p.id !== 'P5' : true));
              const p5Pillar = allPillars.find(p => p.id === 'P5');
              const withEffWeight = (p) => ({ ...p, weight: eff[p.id] != null ? eff[p.id] : p.weight });
              return (
                <Tabs defaultValue="index" className="space-y-4" data-testid="lei-pillar-subtabs">
                  <TabsList className="bg-white p-1 rounded-lg shadow-sm">
                    <TabsTrigger value="index" data-testid="lei-subtab-index">
                      Index (Weighted)
                    </TabsTrigger>
                    <TabsTrigger value="reinforcement" data-testid="lei-subtab-reinforcement">
                      Business Impact — Reinforcement
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="index">
                    <p className="text-xs text-slate-500 mb-3" data-testid="lei-index-caption">
                      {mode === 'conjoined'
                        ? 'Conjoined mode — the LEI index is scored on Pillars 1–4 (weights renormalised to sum to 100%). Pillar 5 is shown under the Reinforcement tab.'
                        : 'Standalone mode — all 5 pillars contribute to the LEI index at their native weights.'}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {indexPillars.map(pillar => (
                        <PillarHealthCard
                          key={pillar.id}
                          pillar={withEffWeight(pillar)}
                          score={pillar.score}
                          trend={pillar.trend}
                          measures={pillar.measures}
                          isExpanded={expandedPillars.includes(pillar.id)}
                          onToggle={() => togglePillar(pillar.id)}
                          pinnedMeasures={pinnedMeasures}
                          onPinMeasure={handlePinMeasure}
                          confidenceMap={confidenceMap}
                          onOpenAudit={openAudit}
                          highlight={highlightPillar === pillar.id}
                        />
                      ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="reinforcement">
                    <Card className="mb-4 border-l-4 border-purple-400 bg-purple-50/40" data-testid="lei-reinforcement-banner">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-purple-800">Pillar 5 · Business Impact & Value Realization</span>
                          {mode === 'conjoined' ? (
                            <Badge className="bg-purple-100 text-purple-700 border border-purple-300" data-testid="lei-p5-nonweighted-chip">
                              0% of index · routed to The Missing Middle
                            </Badge>
                          ) : (
                            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300" data-testid="lei-p5-weighted-chip">
                              10% of index
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-600 mt-1">
                          {mode === 'conjoined'
                            ? 'These measures are captured but not added into the LEI score — they reinforce the downstream value chain (ROE → KPI → ROI) instead, so the same signal is never counted twice.'
                            : 'In standalone mode Pillar 5 also contributes 10% to the LEI index above.'}
                        </p>
                        {mode === 'conjoined' && (p5r?.measures?.length > 0) && (
                          <div className="mt-3 flex flex-wrap gap-2" data-testid="lei-p5-routes">
                            {p5r.measures.map(m => (
                              <span key={m.id} className="inline-flex items-center gap-1 text-xs bg-white border border-purple-200 rounded-full px-3 py-1">
                                <span className="font-medium text-slate-700">{m.name}</span>
                                {m.value != null && <span className="text-slate-400">{Number(m.value).toFixed(0)}</span>}
                                {m.routes_to_label && <span className="text-purple-500">→ {m.routes_to_label}</span>}
                              </span>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    {p5Pillar && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <PillarHealthCard
                          pillar={{ ...p5Pillar, weight: mode === 'conjoined' ? 0 : p5Pillar.weight }}
                          score={p5Pillar.score}
                          trend={p5Pillar.trend}
                          measures={p5Pillar.measures}
                          isExpanded={expandedPillars.includes(p5Pillar.id)}
                          onToggle={() => togglePillar(p5Pillar.id)}
                          pinnedMeasures={pinnedMeasures}
                          onPinMeasure={handlePinMeasure}
                          confidenceMap={confidenceMap}
                          onOpenAudit={openAudit}
                          highlight={highlightPillar === p5Pillar.id}
                        />
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              );
            })()}

            {/* Learning Health Trends — same interactive chart as the Overview */}
            <div className="mt-6" data-testid="lei-deepdive-trend">
              <LeiTrend
                trendData={healthData?.trend_data}
                pillars={healthData?.pillars}
                annotations={healthData?.trend_annotations}
                navigate={navigate}
              />
            </div>
          </TabsContent>
          
          {/* Tab 3: Correlation Matrix */}
          <TabsContent value="correlation">
            <Card>
              <CardHeader>
                <CardTitle>LEI-to-KPI Correlation Matrix</CardTitle>
                <CardDescription>
                  Shows how strongly each LEI measure correlates with business KPIs. 
                  Higher values (green) indicate stronger relationships.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <CorrelationHeatmap correlations={healthData?.correlations} />
              </CardContent>
            </Card>
            
            <Alert className="mt-4">
              <Info className="h-4 w-4" />
              <AlertDescription>
                <strong>How to read:</strong> A correlation of 70%+ means changes in the LEI measure strongly predict changes in the KPI. 
                Use this to identify which learning investments drive the business outcomes you care about most.
              </AlertDescription>
            </Alert>
          </TabsContent>
          
          {/* Tab 4: Cohort Comparison */}
          <TabsContent value="cohorts">
            {orgReady && (
            <CohortComparisonView organizationId={selectedOrg} isDark={isDark} navigate={navigate} />
            )}
          </TabsContent>

          {/* Tab 4b: Program Comparison */}
          <TabsContent value="programs">
            {orgReady && <CohortComparisonView
              organizationId={selectedOrg}
              isDark={isDark}
              navigate={navigate}
              config={{
                endpoint: '/api/lei-health/program-pillars',
                pdfEndpoint: '/api/lei-health/program-pillars/pdf',
                nounSingular: 'program',
                nounPlural: 'programs',
                title: 'Program LEI Pillar Health',
                chartTitle: 'Program LEI — pillar contribution vs benchmark',
                t: 'program',
                filterPrefix: 'program_filter',
                csvName: 'program-lei-comparison',
                emptyHint: 'Create programs and link cohorts (Segments → Programs), then import LEI measures to compare them here.',
                defaultSort: 'roi_eff',
              }}
            />}
          </TabsContent>
          
          {/* Tab 5: Risk & Gap Analysis */}
          <TabsContent value="risks">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="text-rose-500" size={20} />
                    Risk Areas
                  </CardTitle>
                  <CardDescription>Measures requiring immediate attention</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {healthData?.risks?.map((risk, idx) => (
                    <RiskItem key={risk.name || `risk-${idx}`} risk={risk} navigate={navigate} />
                  ))}
                  {(!healthData?.risks || healthData.risks.length === 0) && (
                    <p className="text-center text-gray-500 py-8">No significant risks detected</p>
                  )}
                </CardContent>
              </Card>

              <Card data-testid="lei-priorities-action-plan">
                <CardHeader>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Target className="text-indigo-500" size={20} />
                        Action Plan
                      </CardTitle>
                      <CardDescription>Recommended plan for the weakest pillar</CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      <GoalRing current={Math.round(healthData?.overall_score || 0)} entityId="portfolio" isDark={isDark} size={30} testId="priorities-goal-ring" />
                      <TakeActionButton focus={buildLeiFocus(healthData)} navigate={navigate} testId="lei-priorities-take-action" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {weakestPillar?.id ? (
                    <LEIActionPlanCard
                      pillarId={weakestPillar.id}
                      scoreOverride={weakestPillar.score}
                      overallScore={healthData?.overall_score}
                    />
                  ) : (
                    <p className="text-center text-gray-500 py-8">No priority pillar identified.</p>
                  )}
                </CardContent>
              </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Target className="text-blue-500" size={20} />
                    Gap to Target
                  </CardTitle>
                  <CardDescription>Distance from target scores</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {healthData?.pillars?.flatMap(p => p.measures)
                      .sort((a, b) => (80 - a.score) - (80 - b.score))
                      .slice(-5)
                      .reverse()
                      .map(measure => (
                        <div key={measure.id} className="flex items-center gap-4">
                          <div className="shrink-0 text-sm text-gray-600 whitespace-nowrap">{measure.name}</div>
                          <div className="flex-1 min-w-[4rem] h-4 rounded-full overflow-hidden" style={{ backgroundColor: isDark ? '#1f2a44' : '#e2e8f0' }}>
                            <div
                              className="h-full rounded-full transition-all"
                              style={{ width: `${measure.score}%`, background: 'linear-gradient(to right, #fb7185, #34d399)' }}
                            />
                          </div>
                          <div className="shrink-0 text-right whitespace-nowrap">
                            <span className="font-semibold">{measure.score}</span>
                            <span className={cn('text-sm', isDark ? 'text-slate-400' : 'text-gray-400')}> / 80</span>
                          </div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Tab 6: Investment Efficiency */}
          <TabsContent value="investment">
            <InvestmentView organizationId={selectedOrg} isDark={isDark} navigate={navigate} />
          </TabsContent>
          
          {/* Tab 7: Forecast */}
          <TabsContent value="forecast">
            <ForecastView organizationId={selectedOrg} isDark={isDark} navigate={navigate} />
          </TabsContent>
          
          {/* Tab 8: Score Trends */}
          <TabsContent value="score-trends">
            <ScoreTrendsView organizationId={selectedOrg} isDark={isDark} navigate={navigate} />
          </TabsContent>
        </Tabs>
        </CollapsibleSection>
        </>)}
        </div>
        )}
      </div>
      <MeasureAuditDrawer
        open={auditOpen}
        onOpenChange={setAuditOpen}
        measureId={auditMeasureId}
        organizationId={selectedOrg !== 'all' ? selectedOrg : undefined}
      />
      <AuditPackDialog
        open={packDialogOpen}
        onOpenChange={setPackDialogOpen}
        organizationId={selectedOrg}
        defaultTab={packDialogTab}
        key={packDialogTab}
      />
    </PageShell>
  );
}